import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';

export interface ApiError {
  message: string;
  code?: string;
  field?: string;
  details?: Record<string, unknown> | unknown[];
}

export const getApiErrorMessage = (
  responseData: any,
  defaultMessage: string = 'An error occurred'
): string => {
  if (!responseData) return defaultMessage;
  if (typeof responseData.error === 'string' && responseData.error.trim()) {
    return responseData.error;
  }

  if (
    typeof responseData.error === 'object' &&
    responseData.error !== null &&
    typeof responseData.error.message === 'string' &&
    responseData.error.message.trim()
  ) {
    return responseData.error.message;
  }
  if (Array.isArray(responseData.errors) && responseData.errors.length > 0) {
    const firstErr = responseData.errors[0];
    if (typeof firstErr === 'string' && firstErr.trim()) {
      return firstErr;
    }
    if (
      typeof firstErr === 'object' &&
      firstErr !== null &&
      typeof firstErr.message === 'string' &&
      firstErr.message.trim()
    ) {
      return firstErr.message;
    }
  }
  if (typeof responseData.message === 'string' && responseData.message.trim()) {
    return responseData.message;
  }

  return defaultMessage;
};

export const handleApiError = (error: AxiosError | unknown): ApiError => {
  if (!isAxiosError(error)) {
    const message =
      (error as any)?.message || 'Network error. Please check your connection.';
    const networkError: ApiError = {
      message,
      code: 'NETWORK_ERROR',
    };
    toast.error(networkError.message, { id: 'api-error' });
    return networkError;
  }

  const { response } = error;
  if (!response) {
    const networkError: ApiError = {
      message: 'Network error. Please check your connection.',
      code: 'NETWORK_ERROR',
    };
    toast.error(networkError.message, { id: 'api-error' });
    return networkError;
  }

  const { status, data } = response;
  const responseData = data as any;
  const errorMessage = getApiErrorMessage(
    responseData,
    `Request failed with status ${status}`
  );

  switch (status) {
    case 400: {
      toast.error(errorMessage, { id: 'api-error' });
      return {
        message: errorMessage,
        code: 'BAD_REQUEST',
        details: responseData?.errors,
      };
    }

    case 401: {
      return {
        message: errorMessage || 'Session expired. Please login again.',
        code: 'UNAUTHORIZED',
      };
    }

    case 403: {
      const msg =
        errorMessage ||
        'Access denied. You do not have permission to perform this action.';
      toast.error(msg, { id: 'api-error' });
      return {
        message: msg,
        code: 'FORBIDDEN',
      };
    }

    case 404: {
      const msg = errorMessage || 'Resource not found.';
      toast.error(msg, { id: 'api-error' });
      return {
        message: msg,
        code: 'NOT_FOUND',
      };
    }

    case 405: {
      const msg =
        errorMessage || 'Method not allowed. Please check your request method.';
      toast.error(msg, { id: 'api-error' });
      return {
        message: msg,
        code: 'METHOD_NOT_ALLOWED',
      };
    }

    case 422: {
      toast.error(errorMessage, { id: 'api-error' });
      return {
        message: errorMessage,
        code: 'VALIDATION_ERROR',
        details: responseData?.errors,
      };
    }

    case 429: {
      const msg = errorMessage || 'Too many requests. Please try again later.';
      toast.error(msg, { id: 'api-error' });
      return {
        message: msg,
        code: 'RATE_LIMIT',
      };
    }

    case 500: {
      toast.error(errorMessage, { id: 'api-error' });
      return {
        message: errorMessage,
        code: 'SERVER_ERROR',
      };
    }

    default: {
      toast.error(errorMessage, { id: 'api-error' });
      return {
        message: errorMessage,
        code: `HTTP_${status}`,
      };
    }
  }
};

export const isNetworkError = (error: {
  response?: unknown;
  request?: unknown;
}): boolean => {
  return !error.response && !!error.request;
};

export const isServerError = (error: {
  response?: { status?: number };
}): boolean => {
  return !!(
    error.response &&
    typeof error.response.status === 'number' &&
    error.response.status >= 500
  );
};

export const isClientError = (error: {
  response?: { status?: number };
}): boolean => {
  return !!(
    error.response &&
    typeof error.response.status === 'number' &&
    error.response.status >= 400 &&
    error.response.status < 500
  );
};

export const isAuthError = (error: {
  response?: { status?: number };
}): boolean => {
  return !!(
    error.response &&
    typeof error.response.status === 'number' &&
    error.response.status === 401
  );
};

export const isValidationError = (error: {
  response?: { status?: number };
}): boolean => {
  return !!(
    error.response &&
    typeof error.response.status === 'number' &&
    error.response.status === 422
  );
};

const isAxiosError = (error: unknown): error is AxiosError => {
  return axios.isAxiosError(error);
};
